import { useEffect, useState } from 'react';
import logo from '../../../assets/digisaka-official-logo.png';
import ThemeToggle from '../../shared/components/ThemeToggle';
import { useTheme } from '../../shared/theme/ThemeContext';
import {
  DASH_FONT_SERIF,
  forestTopbarClass,
  forestTopbarIconButtonClass,
} from '../../shared/theme/themeStyles';
import {
  getVisibleAnalysisNavIcons,
  RAIL_TAB_META,
  RailToolIcon,
  type MapToolRailIcon,
} from '../../shared/components/MapToolRail';
import { NAV_PAGE_COPY } from './DashboardNavSidebar';
import { DRONE_PROCESSING_COMING_SOON } from '../../drone-processing/comingSoon';
import ToolbarUserMenu from './ToolbarUserMenu';
import type { AnalysisHeaderSection } from './AnalysisNavBar';
import NotificationsPopover, { NotificationDot, NotificationsPanelBody } from './NotificationsPopover';

interface DashboardTopbarProps {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onStartTour: () => void;
  onOpenProfile: () => void;
  activeAnalysisIcon: MapToolRailIcon | null;
  onAnalysisIconClick: (icon: MapToolRailIcon) => void;
  allowedEntitlementSet?: Set<string> | null;
  navFocused?: boolean;
  /** Settings page is open: the title shows Settings instead of the active tool. */
  settingsOpen?: boolean;
  navToolLabel?: string;
  navSections?: AnalysisHeaderSection[];
  activeNavSectionId?: string | null;
  pinnedNavSectionId?: string | null;
  onNavSectionClick?: (id: string) => void;
  onNavSectionInfo?: (id: string) => void;
  onCloseNavTool?: () => void;
  onCloseNavSection?: () => void;
  /** Replaces the tool title while a sidebar page such as drone processing is open. */
  pageOverride?: { title: string; subtitle: string } | null;
  /** Phone menu entry that opens the same left panel as the green side menu. */
  onOpenDroneProcessing?: () => void;
  droneProcessingActive?: boolean;
  showCarbonProjects?: boolean;
  carbonProjectsActive?: boolean;
  onOpenCarbonProjects?: () => void;
}

function menuRowClass(isDark: boolean) {
  return `flex w-full items-center gap-3 rounded-[14px] px-3 py-2.5 text-left text-[13.5px] font-medium transition-colors ${
    isDark
      ? 'text-[#e6edf3] hover:bg-white/[0.06]'
      : 'text-[#111827] hover:bg-[#f3f4f6]'
  }`;
}

export default function DashboardTopbar({
  sidebarOpen,
  onToggleSidebar,
  onStartTour,
  onOpenProfile,
  activeAnalysisIcon,
  onAnalysisIconClick,
  allowedEntitlementSet = null,
  navFocused = false,
  settingsOpen = false,
  navToolLabel,
  navSections = [],
  activeNavSectionId = null,
  pinnedNavSectionId = null,
  onNavSectionClick,
  onCloseNavTool,
  onNavSectionInfo,
  pageOverride = null,
  onOpenDroneProcessing,
  droneProcessingActive = false,
  showCarbonProjects = false,
  carbonProjectsActive = false,
  onOpenCarbonProjects,
}: DashboardTopbarProps) {
  const { theme, isDark } = useTheme();
  const iconBtn = forestTopbarIconButtonClass(theme);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileNotificationsOpen, setMobileNotificationsOpen] = useState(false);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileNotificationsOpen(false);
  };

  /** Desktop bar sits on a light/dark surface next to the green side menu, not on green. */
  const barIconBtn = `flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors duration-150 ${
    isDark
      ? 'border-white/10 bg-white/[0.06] text-[#8b949e] hover:bg-white/[0.1]'
      : 'border-[#e5e7eb] bg-white text-[#008540] hover:bg-[#f3f4f6]'
  }`;

  const actions = (
    <div className="flex items-center gap-2 shrink-0">
      <button
        type="button"
        onClick={onStartTour}
        aria-label="Take the dashboard tour"
        title="Take the dashboard tour"
        className={barIconBtn}
      >
        <i className="ri-question-line text-base" aria-hidden />
      </button>
      <ThemeToggle size="sm" className="!shadow-none" />
      <div data-tour-id="top-user" className="flex items-center gap-2">
        <NotificationsPopover variant="default" />
        <ToolbarUserMenu variant="forest" onOpenProfile={onOpenProfile} />
      </div>
    </div>
  );

  const pageCopy = pageOverride
    ? pageOverride
    : settingsOpen
    ? { title: 'Settings', subtitle: 'Your account, plan and display preferences' }
    : activeAnalysisIcon
      ? NAV_PAGE_COPY[activeAnalysisIcon]
      : { title: 'Digisaka', subtitle: 'Choose an analysis tool from the menu' };

  /** The open layer's "about" button sits beside the page title instead of in the side menu. */
  const infoSection =
    navFocused && !settingsOpen && activeNavSectionId
      ? navSections.find((section) => section.id === activeNavSectionId && section.hasInfo)
      : undefined;

  const wordmark = (
    <img
      src={logo}
      alt="Digisaka"
      className="h-9 w-auto shrink-0 object-contain object-left lg:h-10"
    />
  );

  return (
    <>
      <header
        className={`relative z-[80] hidden h-[68px] shrink-0 items-center gap-3 overflow-visible border-b px-5 lg:grid lg:grid-cols-[auto_minmax(0,1fr)_auto] ${
          isDark
            ? 'border-[#2d3748] bg-[#161b22] text-[#e6edf3]'
            : 'border-[#e5e7eb] bg-white text-[#111827]'
        }`}
      >
        <div className="flex h-full items-center gap-3 justify-self-start overflow-visible">
          <button
            type="button"
            onClick={onToggleSidebar}
            className={barIconBtn}
            title={sidebarOpen ? 'Hide panel' : 'Show panel'}
            aria-label={sidebarOpen ? 'Hide panel' : 'Show panel'}
          >
            <i className={sidebarOpen ? 'ri-menu-fold-line text-base' : 'ri-menu-unfold-line text-base'} aria-hidden />
          </button>
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <h1 className="truncate text-xl font-bold leading-tight tracking-tight">{pageCopy.title}</h1>
              {infoSection && (
                <button
                  type="button"
                  onClick={() => onNavSectionInfo?.(infoSection.id)}
                  aria-label={`About ${infoSection.label}`}
                  title={`About ${infoSection.label}`}
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors duration-150 ${
                    isDark
                      ? 'border-[#2d3748] text-[#34d399] hover:bg-white/[0.08]'
                      : 'border-[#e5e7eb] text-[#008540] hover:bg-[#f3f4f6]'
                  }`}
                >
                  <i className="ri-information-line text-[15px] leading-none" aria-hidden />
                </button>
              )}
            </div>
            <p className={`truncate text-[13px] leading-snug ${isDark ? 'text-[#8b949e]' : 'text-[#6B7280]'}`}>
              {pageCopy.subtitle}
            </p>
          </div>
        </div>
        {/* Tools and their layers live in the left menu; nothing sits in the middle of the bar. */}
        <div className="min-w-0" aria-hidden />
        <div className="justify-self-end">{actions}</div>
      </header>

      <header className={`relative z-[80] space-y-1.5 border-0 px-3 py-2 lg:hidden ${forestTopbarClass(theme)}`}>
        <div className="flex items-center justify-between gap-2">
          {wordmark}
          <button
            type="button"
            data-tour-id="top-user"
            onClick={() => setMobileMenuOpen(true)}
            className={`${iconBtn} shrink-0`}
            aria-label="Open menu"
            aria-expanded={mobileMenuOpen}
            title="Menu"
          >
            <i className="ri-menu-line text-lg" aria-hidden />
          </button>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[240] lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/45"
            aria-label="Close menu"
            onClick={closeMobileMenu}
          />
          <div
            className={`absolute inset-y-0 right-0 flex w-[min(100%,22rem)] flex-col shadow-2xl ${
              isDark ? 'bg-[#161b22] text-[#e6edf3]' : 'bg-white text-[#111827]'
            }`}
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
          >
            <div
              className={`flex shrink-0 items-center justify-between px-4 py-3 ${
                isDark ? 'border-b border-[#2d3748]' : 'border-b border-[#e5e7eb]'
              }`}
            >
              <p className="text-[17px] font-semibold" style={{ fontFamily: DASH_FONT_SERIF }}>
                Menu
              </p>
              <button
                type="button"
                onClick={closeMobileMenu}
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                  isDark ? 'text-[#8b949e] hover:bg-white/[0.06]' : 'text-[#374151] hover:bg-[#f3f4f6]'
                }`}
                aria-label="Close menu"
              >
                <i className="ri-close-line text-lg" aria-hidden />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
              <p
                className={`mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.11em] ${
                  isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'
                }`}
              >
                {navFocused ? (pinnedNavSectionId ? 'Open layer' : navToolLabel || 'Open tool') : 'Analysis tools'}
              </p>
              <div className="mb-5 space-y-0.5">
                {navFocused && (
                  <button
                    type="button"
                    className={menuRowClass(isDark)}
                    onClick={() => {
                      closeMobileMenu();
                      onCloseNavTool?.();
                    }}
                  >
                    <i className="ri-close-line text-lg" aria-hidden />
                    Close and clear
                  </button>
                )}
                {navFocused && activeAnalysisIcon === 'droneData' && onOpenDroneProcessing && (
                  <button
                    type="button"
                    aria-disabled={DRONE_PROCESSING_COMING_SOON || undefined}
                    className={`${menuRowClass(isDark)} ${DRONE_PROCESSING_COMING_SOON ? 'cursor-default opacity-60' : ''}`}
                    onClick={() => {
                      if (DRONE_PROCESSING_COMING_SOON) return;
                      closeMobileMenu();
                      onOpenDroneProcessing();
                    }}
                  >
                    <span className="min-w-0 flex-1 text-left">Drone Processing</span>
                    {DRONE_PROCESSING_COMING_SOON && (
                      <span
                        className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none ${
                          isDark ? 'bg-white/15 text-emerald-100' : 'bg-[#e8f6ee] text-[#008540]'
                        }`}
                      >
                        Coming soon
                      </span>
                    )}
                  </button>
                )}
                {navFocused && activeAnalysisIcon === 'droneData' && navSections.length > 0 && (
                  <p
                    className={`px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-[0.08em] ${
                      isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'
                    }`}
                  >
                    Sample Data
                  </p>
                )}
                {navFocused
                  ? navSections.map((section) => (
                      <button
                        key={section.id}
                        type="button"
                        className={`${menuRowClass(isDark)} ${
                          section.id === activeNavSectionId || section.id === pinnedNavSectionId
                            ? 'bg-[#008540] text-white'
                            : ''
                        }`}
                        onClick={() => {
                          closeMobileMenu();
                          if (!pinnedNavSectionId && navSections.length > 1) onNavSectionClick?.(section.id);
                        }}
                      >
                        {section.label}
                      </button>
                    ))
                  : (
                    <>
                      {showCarbonProjects && (
                        <button
                          type="button"
                          className={`${menuRowClass(isDark)} ${carbonProjectsActive ? 'bg-[#008540] text-white' : ''}`}
                          onClick={() => {
                            closeMobileMenu();
                            onOpenCarbonProjects?.();
                          }}
                        >
                          <span className="whitespace-pre-line text-[13.5px] font-semibold leading-[1.25]">
                            Carbon Projects
                          </span>
                        </button>
                      )}
                      {getVisibleAnalysisNavIcons(allowedEntitlementSet).map((id) => {
                  const meta = RAIL_TAB_META[id];
                  const isActive = activeAnalysisIcon === id || (id === 'droneData' && !!droneProcessingActive);
                  return (
                    <div key={id}>
                      <button
                        type="button"
                        className={`${menuRowClass(isDark)} ${
                          isActive ? 'bg-[#008540] text-white' : ''
                        }`}
                        onClick={() => {
                          closeMobileMenu();
                          onAnalysisIconClick(id);
                        }}
                      >
                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] ${
                            isActive
                              ? 'bg-[rgba(17,24,39,.12)] text-[#111827]'
                              : isDark
                                ? 'bg-[rgba(52,211,153,.16)] text-[#34d399]'
                                : 'bg-[rgba(0,133,64,.12)] text-[#008540]'
                          }`}
                        >
                          <RailToolIcon
                            meta={meta}
                            active={false}
                            theme={isDark && !isActive ? 'dark' : 'light'}
                            ink={isActive ? 'black' : 'auto'}
                            imgClassName="h-[15px] w-[15px] object-contain"
                            iconClassName="text-[15px]"
                          />
                        </span>
                        <span className="whitespace-pre-line text-[13.5px] font-semibold leading-[1.25]">
                          {meta.fullLabel}
                        </span>
                      </button>
                      {id === 'droneData' && onOpenDroneProcessing && (
                        <button
                          type="button"
                          aria-disabled={DRONE_PROCESSING_COMING_SOON || undefined}
                          className={`${menuRowClass(isDark)} pl-14 ${
                            !DRONE_PROCESSING_COMING_SOON && droneProcessingActive ? 'bg-[#008540] text-white' : ''
                          } ${DRONE_PROCESSING_COMING_SOON ? 'cursor-default' : ''}`}
                          onClick={() => {
                            if (DRONE_PROCESSING_COMING_SOON) return;
                            closeMobileMenu();
                            onOpenDroneProcessing();
                          }}
                        >
                          <span className="min-w-0 flex-1 text-left">Drone Processing</span>
                          {DRONE_PROCESSING_COMING_SOON && (
                            <span
                              className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none ${
                                isDark ? 'bg-white/15 text-emerald-100' : 'bg-[#e8f6ee] text-[#008540]'
                              }`}
                            >
                              Coming soon
                            </span>
                          )}
                        </button>
                      )}
                    </div>
                  );
                      })}
                    </>
                  )}
              </div>

              <p
                className={`mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.11em] ${
                  isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'
                }`}
              >
                Account
              </p>
              <ToolbarUserMenu
                inline
                onOpenProfile={() => {
                  closeMobileMenu();
                  onOpenProfile();
                }}
              />

              <p
                className={`mb-2 mt-5 font-mono text-[11px] font-semibold uppercase tracking-[0.11em] ${
                  isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'
                }`}
              >
                Dashboard
              </p>
              <div className="space-y-0.5">
                <button
                  type="button"
                  className={menuRowClass(isDark)}
                  onClick={() => {
                    closeMobileMenu();
                    if (!sidebarOpen) onToggleSidebar();
                  }}
                >
                  <i className="ri-layout-left-line text-lg" aria-hidden />
                  Fields
                </button>
                <button
                  type="button"
                  className={menuRowClass(isDark)}
                  onClick={() => {
                    closeMobileMenu();
                    onStartTour();
                  }}
                >
                  <i className="ri-question-line text-lg" aria-hidden />
                  Help
                </button>
                <div className={`${menuRowClass(isDark)} justify-between`}>
                  <span className="flex items-center gap-3">
                    <i className="ri-palette-line text-lg" aria-hidden />
                    Theme
                  </span>
                  <ThemeToggle size="sm" />
                </div>
                <button
                  type="button"
                  className={menuRowClass(isDark)}
                  onClick={() => setMobileNotificationsOpen((v) => !v)}
                  aria-expanded={mobileNotificationsOpen}
                >
                  <span className="relative">
                    <i className="ri-notification-3-line text-lg" aria-hidden />
                    <NotificationDot className="absolute -right-0.5 -top-0.5" />
                  </span>
                  Notifications
                </button>
                {mobileNotificationsOpen && (
                  <div className="-mx-1 mb-2">
                    <NotificationsPanelBody onClose={() => setMobileNotificationsOpen(false)} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
